import { useRef } from "react";
import { Box, Grid, Stack, useTheme } from "@mui/material";

import { ActiveEvent } from "../ChooseSeats/ActiveEvent";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { InteractiveSeatMap } from "../../InteractiveSeatMap/InteractiveSeatMap";
import { BaseInventoryCard } from "../ChooseSeats/InventoryCards/BaseInventoryCard";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";

import {
  selectSeatCount,
  selectAccessibility,
} from "@/global/store/data/gameFilters";
import { useAppStore } from "@/global/store";
import { throttleTime } from "@/global/utils/utils";
import { createState } from "@/global/store/zustand_store";
import { useInventoryPreview } from "./useInventoryPreview";
import { selectViewId3d } from "@/global/store/data/seatMap";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useChooseSeats } from "../ChooseSeats/useChooseSeats";
import { useEffectAsync } from "@/shared/base/hooks/useEffectAsync";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useSelectActiveEvent } from "../ChooseSeats/useSelectActiveEvent";
import { validateInventorySelection } from "../ChooseSeats/utils.chooseSeats";
import { selectProducts } from "@/global/store/data/purchaseSeats/cartManager";
import { VirtualizedInventoryGridV2 } from "../ChooseSeats/VirtualizedInventoryGridV2";
import { calcDynamicSeatMapStaticImage } from "../../InteractiveSeatMap/utils.seatMap";

import type { EventType, InventoryType } from "@/global/store/data/types";

const useModalState = createState({
  isOpen: false,
  event: null as EventType | null,
  inventories: [] as InventoryType[],
  isInventoryLoading: true,
  isCheckoutLoading: false,
});

export const useInventoryPreviewModal = () => {
  const modalState = useModalState();
  const open = (args: Partial<typeof modalState>) => {
    modalState.set({ isOpen: true, ...args });
  };

  const close = () => {
    modalState.set({ isOpen: false });
    throttleTime(400, () => {
      modalState.reset();
    });
  };
  return { ...modalState, open, close };
};

function withModalOpen(Component: React.ComponentType<any>) {
  return function WrappedComponent(props: any) {
    const previewModal = useInventoryPreviewModal();

    if (previewModal.isOpen) return <Component {...props} />;
    else return null;
  };
}

export const InventoryPreviewModal = withModalOpen(function () {
  const theme = useTheme();
  const { sm: smDown, md: mdDown } = useCssMediaQuery("down");

  const teamConfig = useTeamConfig();
  const { seatMapConfigs, inventoryType, purchaseSeatConfigs } = teamConfig;
  const allowCheckoutFromPreview = purchaseSeatConfigs?.allowCheckoutFromPreview; // prettier-ignore

  const checkoutTriggeredRef = useRef(false);
  const previewModal = useInventoryPreviewModal();
  const previewEvent = previewModal?.event;

  const { activeEvent } = useSelectActiveEvent();
  const seatCount = useAppStore(selectSeatCount);
  const { isAda } = useAppStore(selectAccessibility);
  const cartProducts = useAppStore(selectProducts(previewEvent?.eventCode));
  const selectedProducts = useAppStore(selectProducts(previewEvent?.eventCode)); // prettier-ignore
  const { handleInventorySelect } = useChooseSeats();
  const viewId3dSelected = useAppStore(selectViewId3d);

  const staticSeatMapImage = calcDynamicSeatMapStaticImage(teamConfig, previewEvent);

  const { isInventoriesValidated, isEventValidated } =
    validateInventorySelection(cartProducts, {
      seatCount,
      inventoryType: isAda ? "ADA" : inventoryType,
    });

  const {
    handleInitCheckout,
    handleGetPreviewInventory,
    handleCompleteCheckout,
  } = useInventoryPreview();

  // get inventories
  useEffectAsync({
    deps: [previewModal.event],
    effect: () => handleGetPreviewInventory(previewEvent),
  });

  // firing checkout (only after auth callback sets isCheckoutLoading)
  useEffectAsync({
    deps: [cartProducts, activeEvent],
    effect: () => {
      if (!previewModal?.isCheckoutLoading) return;
      if (!cartProducts?.length || !activeEvent) return;
      if (checkoutTriggeredRef.current) return;
      handleCompleteCheckout();
      checkoutTriggeredRef.current = true;
    },
  });

  function renderNotificationModalBody() {
    if (!allowCheckoutFromPreview) {
      return (
        <VirtualizedInventoryGridV2
          inventories={previewModal?.inventories}
          selectedInventories={[]}
          isLoading={previewModal?.isInventoryLoading}
          renderInventoryCard={(inv) => {
            return (
              <BaseInventoryCard
                inventory={inv}
                isDisabledHoverAnim={mdDown}
                isFooterHidden={true}
                style={{
                  wrapper: {
                    cursor: "default",
                  },
                }}
              />
            );
          }}
        />
      );
    }

    return (
      <VirtualizedInventoryGridV2
        inventories={previewModal?.inventories}
        selectedInventories={selectedProducts}
        isLoading={previewModal?.isInventoryLoading}
        renderInventoryCard={(inv) => {
          const isSelected = selectedProducts
            .map((inv) => inv?.id)
            .includes(inv?.id);

          return (
            <BaseInventoryCard
              inventory={inv}
              isSelected={isSelected}
              isDisabledHoverAnim={mdDown}
              onSelect={() => handleInventorySelect(inv, previewModal?.event)}
              isDisabled={
                (isInventoriesValidated && !isSelected) ||
                previewModal?.isCheckoutLoading
              }
              style={{
                wrapper: {
                  cursor: "pointer",
                  height: "max-content",
                },
              }}
            />
          );
        }}
      />
    );
  }

  function renderNotificationModalFooter() {
    if (!allowCheckoutFromPreview) {
      return (
        <ModalActionButton
          variant="primary"
          style={{ borderRadius: "32px" }}
          onClick={() => previewModal.close()}
        >
          Close
        </ModalActionButton>
      );
    }

    return (
      <>
        <ModalActionButton
          fullWidth={smDown ? true : false}
          variant="primary"
          style={{ borderRadius: "32px" }}
          onClick={handleInitCheckout}
          isLoading={previewModal?.isCheckoutLoading}
          isDisabled={!isEventValidated || previewModal?.isCheckoutLoading}
        >
          Proceed to Checkout
        </ModalActionButton>
      </>
    );
  }

  return (
    <NotificationModal
      width="xl"
      isOpen={previewModal?.isOpen}
      hideTitleIcon={true}
      onClose={() => previewModal.close()}
      closeIconProps={{ isDisabled: previewModal?.isCheckoutLoading }}
      renderBody={() => (
        <Box p={smDown ? 2 : 4} mt={smDown ? 11 : 0} overflow={"hidden"}>
          <Grid container spacing={4}>
            {staticSeatMapImage && !viewId3dSelected ? (
              <Grid item xs={12} md={6} height={"fit-content"}>
                <Box
                  component={"img"}
                  src={staticSeatMapImage}
                  alt="Seat Map"
                  width={"100%"}
                  {...seatMapConfigs?.customSeatMapStaticImageStyle}
                />
              </Grid>
            ) : (
              <Grid item xs={12} md={6} height={"fit-content"}>
                <InteractiveSeatMap event={previewEvent} />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              {renderNotificationModalBody()}
            </Grid>
          </Grid>
        </Box>
      )}
      renderHeader={() => (
        <Box
          sx={{
            px: 2,
            py: 1,
            position: mdDown ? "fixed" : "relative",
            top: 0,
            width: "100%",
            backgroundColor: theme.palette.background.paper,
            zIndex: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {previewModal.event ? (
            <ActiveEvent event={previewModal.event} />
          ) : null}
        </Box>
      )}
      renderFooter={() => (
        <Stack
          sx={{
            py: 2,
            px: 3,
            rowGap: 2,
            bottom: 0,
            columnGap: 2,
            width: "100%",
            flexWrap: "wrap",
            flexDirection: "row",
            justifyContent: "flex-end",
            position: mdDown ? "fixed" : "relative",
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {renderNotificationModalFooter()}
        </Stack>
      )}
      bodyProps={{
        hideTopDivider: true,
        hideBottomDivider: true,
      }}
      dialogProps={{
        fullScreen: mdDown,
      }}
      paperProps={{
        style: {
          marginLeft: !mdDown ? 2 : 0,
          marginRight: !mdDown ? 2 : 0,
        },
      }}
    />
  );
});
